import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { GraphData, GraphNode, GraphEdge } from "../types/graph";
import { graphApi } from "../api/graphApi";

export interface GraphContextValue {
  graphData: GraphData | null;
  loading: boolean;
  error: string | null;
  fetchGraph(repoId: string): Promise<void>;
  expandNode(nodeId: string, hops: number): Promise<void>;
}

const GraphContext = createContext<GraphContextValue | null>(null);

interface GraphProviderProps {
  projectId: string;
  repoId: string;
  children: ReactNode;
}

export function GraphProvider({ projectId, repoId, children }: GraphProviderProps) {
  const [graphData, setGraphData] = useState<GraphData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track previous project/repo to detect changes and clear cache
  const prevScopeRef = useRef<string>("");

  // Clear cached data when project_id or repo_id changes (Requirement 10.5)
  useEffect(() => {
    const currentScope = `${projectId}:${repoId}`;
    if (prevScopeRef.current && prevScopeRef.current !== currentScope) {
      setGraphData(null);
      setError(null);
      setLoading(false);
    }
    prevScopeRef.current = currentScope;
  }, [projectId, repoId]);

  const fetchGraph = useCallback(
    async (fetchRepoId: string) => {
      setLoading(true);
      setError(null);
      try {
        const data = await graphApi.getFullGraph(fetchRepoId);
        setGraphData(data);
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to fetch graph";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const expandNode = useCallback(
    async (nodeId: string, hops: number) => {
      setLoading(true);
      setError(null);
      try {
        const neighbors = await graphApi.getNeighbors(repoId, nodeId, hops);
        setGraphData((prev) => {
          if (!prev) return neighbors;
          return mergeGraphData(prev, neighbors);
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to expand node";
        setError(message);
      } finally {
        setLoading(false);
      }
    },
    [repoId]
  );

  const value: GraphContextValue = {
    graphData,
    loading,
    error,
    fetchGraph,
    expandNode,
  };

  return <GraphContext.Provider value={value}>{children}</GraphContext.Provider>;
}

export function useGraphContext(): GraphContextValue {
  const ctx = useContext(GraphContext);
  if (!ctx) {
    throw new Error("useGraphContext must be used within a GraphProvider");
  }
  return ctx;
}

/** Merge new graph data into existing, deduplicating by id */
function mergeGraphData(existing: GraphData, incoming: GraphData): GraphData {
  const nodeMap = new Map<string, GraphNode>();
  for (const node of existing.nodes) nodeMap.set(node.id, node);
  for (const node of incoming.nodes) nodeMap.set(node.id, node);

  const edgeMap = new Map<string, GraphEdge>();
  for (const edge of existing.edges) edgeMap.set(edge.id, edge);
  for (const edge of incoming.edges) edgeMap.set(edge.id, edge);

  return {
    nodes: Array.from(nodeMap.values()),
    edges: Array.from(edgeMap.values()),
  };
}
